import { useState } from "react";
import {
  type Identifier,
  useCreate,
  useDelete,
  useGetList,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Confirm } from "@/components/admin/confirm";

import { channelColorStyle } from "../notes/channelColors";
import { ChannelDialog, type ChannelDraft } from "../notes/ChannelDialog";
import { findChannelByName, isBuiltInChannel } from "../notes/noteChannels";
import type { NoteChannel } from "../types";

// PostgREST returns each embedded aggregate as an array holding one row. The
// demo provider ignores meta.columns, so there the fields are simply absent,
// and a missing count must not be shown as zero (see TagsSection).
type ChannelWithUsage = NoteChannel & {
  contact_notes?: { count: number }[];
  deal_notes?: { count: number }[];
  company_notes?: { count: number }[];
};

const usageCount = (channel: ChannelWithUsage) => {
  const counts = [
    channel.contact_notes,
    channel.deal_notes,
    channel.company_notes,
  ].map((rows) => rows?.[0]?.count);
  if (counts.every((count) => count === undefined)) return undefined;
  return counts.reduce<number>((sum, count) => sum + (count ?? 0), 0);
};

const NOTE_RESOURCES = ["contact_notes", "deal_notes", "company_notes"];

/**
 * Settings, Note Channels: add, edit (name and colour) and delete the channels
 * a note can be given. A table of its own, like tags, so each change is saved as it is made
 * rather than with the page's Save. LCCRM-87.
 *
 * Deleting a channel clears it from its notes and keeps the notes. Email has
 * no delete: the inbound email link assigns it, and the database refuses.
 *
 * Also opened from the note form's Channel dropdown ("Manage channels"), which
 * passes `onDeleted` to drop a deleted channel from the note being written.
 */
export const ChannelsSection = ({
  onDeleted,
}: {
  onDeleted?: (id: Identifier) => void;
} = {}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const { data: channels, refetch } = useGetList<ChannelWithUsage>(
    "note_channels",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "name", order: "ASC" },
      meta: {
        columns: [
          "id",
          "name",
          "key",
          "color",
          "contact_notes(count)",
          "deal_notes(count)",
          "company_notes(count)",
        ],
      },
    },
  );

  const [create] = useCreate<NoteChannel>();
  const [update] = useUpdate<NoteChannel>();
  const [deleteOne, { isPending: isDeleting }] = useDelete<NoteChannel>();
  const [createOpen, setCreateOpen] = useState(false);
  const [toEdit, setToEdit] = useState<ChannelWithUsage | null>(null);
  const [toDelete, setToDelete] = useState<ChannelWithUsage | null>(null);

  const duplicateError = () =>
    new Error(
      translate("resources.notes.channel.duplicate", {
        _: "A channel with this name already exists",
      }),
    );

  // Every other list of channels in the app (the note form's picker, the
  // badges on notes) was fetched before this change.
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["note_channels"] });
    await refetch();
  };

  const handleCreate = async ({ name, color }: ChannelDraft) => {
    if (findChannelByName(channels, name)) throw duplicateError();
    await create(
      "note_channels",
      { data: { name, color } },
      { returnPromise: true },
    );
    setCreateOpen(false);
    await refresh();
  };

  const handleEdit = async ({ name, color }: ChannelDraft) => {
    if (!toEdit) return;
    const taken = findChannelByName(channels, name);
    if (taken && taken.id !== toEdit.id) throw duplicateError();
    await update(
      "note_channels",
      // These two alone: they are the only columns a client may write.
      { id: toEdit.id, data: { name, color }, previousData: toEdit },
      { returnPromise: true },
    );
    setToEdit(null);
    await refresh();
  };

  const handleDelete = () => {
    if (!toDelete) return;
    deleteOne(
      "note_channels",
      { id: toDelete.id, previousData: toDelete },
      {
        onSuccess: async () => {
          onDeleted?.(toDelete.id);
          setToDelete(null);
          // The notes that had it now have none.
          for (const resource of [...NOTE_RESOURCES, "activity_log"]) {
            void queryClient.invalidateQueries({ queryKey: [resource] });
          }
          await refresh();
        },
        onError: (error: any) => {
          notify(
            error?.message ||
              translate("resources.notes.channel.delete_error", {
                _: "Couldn't delete the channel",
              }),
            { type: "error" },
          );
        },
      },
    );
  };

  const deleteCount = toDelete ? usageCount(toDelete) : undefined;
  const deleteContent =
    deleteCount === undefined
      ? {
          key: "crm.settings.channels.delete_content_unknown",
          options: {
            _: "Notes that have this channel keep their text and lose the channel.",
          },
        }
      : deleteCount === 0
        ? {
            key: "crm.settings.channels.delete_content_unused",
            options: { _: "No note has this channel." },
          }
        : {
            key: "crm.settings.channels.delete_content",
            options: {
              _: "%{smart_count} note has this channel. It keeps its text and loses the channel. |||| %{smart_count} notes have this channel. They keep their text and lose the channel.",
              smart_count: deleteCount,
            },
          };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-muted-foreground">
            {translate("crm.settings.sections.note_channels", {
              _: "Note Channels",
            })}
          </h2>
          <p className="text-sm text-muted-foreground">
            {translate("crm.settings.channels.description", {
              _: "Where a note's conversation happened. Pick one when writing a note; notes saved by the inbound email link get Email on their own.",
            })}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="h-4 w-4 mr-1" />
          {translate("resources.notes.channel.new_title", {
            _: "New channel",
          })}
        </Button>
      </div>

      <div className="space-y-1">
        {channels?.map((channel) => {
          const count = usageCount(channel);
          return (
            <div
              key={channel.id}
              className="flex items-center justify-between gap-2 py-1 px-2 rounded hover:bg-muted"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Badge
                  variant="outline"
                  className="text-xs font-normal"
                  style={channelColorStyle(channel.color)}
                >
                  {channel.name}
                </Badge>
                {count !== undefined && (
                  <span className="text-xs text-muted-foreground">
                    {translate("crm.settings.channels.usage", {
                      smart_count: count,
                      _: "%{smart_count} note |||| %{smart_count} notes",
                    })}
                  </span>
                )}
              </div>
              <div className="flex shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={translate("crm.settings.channels.edit", {
                    name: channel.name,
                    _: "Edit %{name}",
                  })}
                  onClick={() => setToEdit(channel)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                {isBuiltInChannel(channel) ? (
                  // Keeps the pencils in one column.
                  <span className="w-9" aria-hidden="true" />
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={translate("crm.settings.channels.delete", {
                      name: channel.name,
                      _: "Delete %{name}",
                    })}
                    onClick={() => setToDelete(channel)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {createOpen && (
        <ChannelDialog
          title={translate("resources.notes.channel.new_title", {
            _: "New channel",
          })}
          confirmLabel={translate("resources.notes.channel.create", {
            _: "Create channel",
          })}
          onSubmit={handleCreate}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {toEdit && (
        <ChannelDialog
          title={translate("crm.settings.channels.edit", {
            name: toEdit.name,
            _: "Edit %{name}",
          })}
          initialName={toEdit.name}
          initialColor={toEdit.color ?? null}
          confirmLabel={translate("ra.action.save")}
          onSubmit={handleEdit}
          onClose={() => setToEdit(null)}
        />
      )}

      <Confirm
        isOpen={toDelete !== null}
        loading={isDeleting}
        confirmColor="warning"
        title="crm.settings.channels.delete_title"
        titleTranslateOptions={{
          _: 'Delete "%{name}"?',
          name: toDelete?.name ?? "",
        }}
        content={deleteContent.key}
        contentTranslateOptions={deleteContent.options}
        onConfirm={handleDelete}
        onClose={() => setToDelete(null)}
      />
    </div>
  );
};
